/** Dymo-friendly presets + custom size (inches). Used by API and POS web preview. */
export const LABEL_SIZE_PRESET_IDS = ['2.25x4', '2.25x1.25', '1.125x3.5', 'custom'] as const;
export type LabelSizePresetId = (typeof LABEL_SIZE_PRESET_IDS)[number];

const PRESETS: Record<Exclude<LabelSizePresetId, 'custom'>, { widthIn: number; heightIn: number }> = {
  /** Default repair / ticket label */
  '2.25x4': { widthIn: 2.25, heightIn: 4 },
  /** Standard address */
  '2.25x1.25': { widthIn: 2.25, heightIn: 1.25 },
  /** Standard shipping / file folder */
  '1.125x3.5': { widthIn: 1.125, heightIn: 3.5 },
};

export const LABEL_SIZE_PRESET_OPTIONS: ReadonlyArray<{ id: LabelSizePresetId; label: string }> = [
  { id: '2.25x4', label: `Repair ticket (${PRESETS['2.25x4'].widthIn}" × ${PRESETS['2.25x4'].heightIn}")` },
  { id: '2.25x1.25', label: `Address (${PRESETS['2.25x1.25'].widthIn}" × ${PRESETS['2.25x1.25'].heightIn}")` },
  { id: '1.125x3.5', label: `Shipping / narrow (${PRESETS['1.125x3.5'].widthIn}" × ${PRESETS['1.125x3.5'].heightIn}")` },
  { id: 'custom', label: 'Custom (set width & height below)' },
];

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Accepts `stores.label_config` JSON or a plain object; always returns valid dimensions. */
export function resolveLabelDimensions(raw: unknown): { widthIn: number; heightIn: number } {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

  const presetRaw = o.labelSizePreset;
  const preset: string =
    typeof presetRaw === 'string' && LABEL_SIZE_PRESET_IDS.includes(presetRaw as LabelSizePresetId)
      ? presetRaw
      : '2.25x4';

  if (preset === 'custom') {
    const w = typeof o.labelWidthIn === 'number' && Number.isFinite(o.labelWidthIn) ? o.labelWidthIn : 2.25;
    const h = typeof o.labelHeightIn === 'number' && Number.isFinite(o.labelHeightIn) ? o.labelHeightIn : 4;
    return {
      widthIn: clamp(w, 0.5, 6),
      heightIn: clamp(h, 0.5, 12),
    };
  }

  if (preset in PRESETS) {
    const p = PRESETS[preset as Exclude<LabelSizePresetId, 'custom'>];
    return { widthIn: p.widthIn, heightIn: p.heightIn };
  }

  return { ...PRESETS['2.25x4'] };
}
